import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { StripeCardNumberElementChangeEvent } from '@stripe/stripe-js';
import { Donation } from './DonationType';
import DonationApi from './DonationApi';

import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Heart,
  Rocket,
  Zap,
  Coffee,
  Star,
  Plus,
  Minus,
  FileText,
  PartyPopper,
  Gift
} from 'lucide-react';

import { getAccountIdFromSessionStorage } from '../lib/global_function';
import { cardStyle } from '../components/common/CreditCard';
import { ReactComponent as PoweredByStripe } from '../components/image/creditCard/powered_by_stripe.svg';
import BackofCard from '../components/image/creditCard/back-of-card.png';
import Visa from '../components/image/creditCard/visa.png';
import MasterCard from '../components/image/creditCard/master.png';
import Amex from '../components/image/creditCard/amx.png';
import Discover from '../components/image/creditCard/discover.png';
import RedCross from '../components/image/creditCard/wrong_card.png';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string);

const breadcrumbs = [
  { label: 'Account', path: '/main/account' },
  { label: 'My Donation', path: '/main/account/donation' },
  { label: 'Support Us', path: '' },
];

// Fun messages that change based on donation amount
const getDonationMessage = (multiplier: number): string => {
  if (multiplier === 1) return "☕ A coffee's worth of support!";
  if (multiplier <= 3) return "🌟 You're a star supporter!";
  if (multiplier <= 5) return "🚀 Helping us reach new heights!";
  if (multiplier <= 10) return "💪 Power supporter status!";
  if (multiplier <= 20) return "🏆 Champion level generosity!";
  if (multiplier <= 50) return "👑 Royal supporter territory!";
  return "🦸 Superhero level donation!";
};

const cardIcons: Record<string, string> = {
  visa: Visa,
  mastercard: MasterCard,
  amex: Amex,
  discover: Discover,
};

const CreateDonationForm = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [multiplier, setMultiplier] = useState(1);
  const [createdDonation, setCreatedDonation] = useState<Donation | null>(null);

  const [accountId, setAccountId] = useState<string | null>(null);

  // Stripe card state
  const [cardBrand, setCardBrand] = useState('');
  const [isCardComplete, setIsCardComplete] = useState(false);

  const AMOUNT_PER_UNIT = 5;
  const MIN_MULTIPLIER = 1;
  const MAX_MULTIPLIER = 100;

  useEffect(() => {
    const id = getAccountIdFromSessionStorage(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
    setAccountId(id);
  }, []);

  const totalAmount = multiplier * AMOUNT_PER_UNIT;

  const handleIncrement = () => {
    if (multiplier < MAX_MULTIPLIER) {
      setMultiplier(prev => prev + 1);
    }
  };

  const handleDecrement = () => {
    if (multiplier > MIN_MULTIPLIER) {
      setMultiplier(prev => prev - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      if (value < MIN_MULTIPLIER) {
        setMultiplier(MIN_MULTIPLIER);
      } else if (value > MAX_MULTIPLIER) {
        setMultiplier(MAX_MULTIPLIER);
      } else {
        setMultiplier(value);
      }
    }
  };

  const handleCardChange = (event: StripeCardNumberElementChangeEvent) => {
    setCardBrand(event.brand || '');
    setIsCardComplete(event.complete);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!stripe || !elements) {
      setErrorMessage('Stripe has not been initialized.');
      return;
    }

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      setErrorMessage('Card input is not ready.');
      return;
    }

    if (!accountId) {
      setErrorMessage('Please log in to make a donation.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create payment method with Stripe
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber,
      });

      if (error || !paymentMethod) {
        setErrorMessage(error?.message || 'Failed to create payment method.');
        setIsSubmitting(false);
        return;
      }

      // Create donation with payment method
      const donationPayload: Partial<Donation> = {
        account_id: accountId,
        amount: totalAmount,
        stripe_payment_method_id: paymentMethod.id,
      };

      const donation = await DonationApi.createDonation(donationPayload);
      setCreatedDonation(donation);
      setSuccessMessage(`Thank you for your generous $${totalAmount} donation! 🎉`);

    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewDonation = () => {
    setCreatedDonation(null);
    setSuccessMessage(null);
    setMultiplier(1);
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-4">
        <Breadcrumbs items={breadcrumbs} />


        <div className="mt-4 max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-primary rounded-full mb-2 shadow-md">
              <Heart className="w-6 h-6 text-white" fill="white" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Support MrJoe Pro</h2>
            <p className="text-sm text-gray-600">Your generosity fuels our mission</p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error" className="mb-4">
              {errorMessage}
            </Alert>
          )}

          {/* Success Message */}
          {successMessage && (
            <Alert variant="success" className="mb-4">
              {successMessage}
            </Alert>
          )}

          {/* Post-Donation View */}
          {createdDonation ? (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
              <PartyPopper className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-green-800 mb-1">
                Thank You, Amazing Supporter!
              </h3>
              <p className="text-green-700 mb-4">
                Your donation of <span className="font-bold text-xl">${createdDonation.amount}</span> has been received.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={handleNewDonation}
                  variant="inverted"
                  size="medium"
                >
                  <Gift size={18} className="mr-1" />
                  Make Another Donation
                </Button>

                <Button
                  onClick={() => navigate('/main/account/donation')}
                  variant="default"
                  size="medium"
                >
                  <FileText size={18} className="mr-1" />
                  View Donations
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Why Donate Section - Compact */}
              <div className="bg-orange-50 border border-primary/30 rounded-xl p-4 mb-4">
                <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4 text-primary" fill="currentColor" />
                  Why Your Support Matters
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Rocket className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-foreground">Faster Performance</span>
                      <p className="text-xs text-gray-600">Upgrade to advanced computing resources</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-foreground">New Features</span>
                      <p className="text-xs text-gray-600">Build exciting new capabilities</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-foreground">Better Service</span>
                      <p className="text-xs text-gray-600">More responsive customer support</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Coffee className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-foreground">Fuel the Team</span>
                      <p className="text-xs text-gray-600">Keep our developers caffeinated!</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Donation Form */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-5 shadow-sm">
                <form onSubmit={handleSubmit}>
                  <h3 className="text-base font-semibold text-center text-foreground mb-4">
                    Choose Your Donation Amount
                  </h3>
                  <div className="text-center text-xs text-gray-500 mb-2">
                    (each lick of plus or minus sign increases or decreases by $5)
                  </div>
                  {/* Amount Selector */}
                  <div className="flex flex-col items-center mb-4">
                    {/* Amount Display */}
                    <div className="text-4xl font-bold text-primary mb-1">
                      ${totalAmount}
                    </div>

                    {/* Dynamic Message */}
                    <p className="text-sm text-gray-600 mb-4">
                      {getDonationMessage(multiplier)}
                    </p>

                    {/* Spinner Control */}
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleDecrement}
                        disabled={multiplier <= MIN_MULTIPLIER}
                        className={`
                          w-10 h-10 rounded-full flex items-center justify-center
                          transition-all duration-200
                          ${multiplier <= MIN_MULTIPLIER
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-primary text-white hover:bg-primary-hover active:bg-primary-active'}
                        `}
                      >
                        <Minus className="w-5 h-5" />
                      </button>

                      <div className="relative">
                        <input
                          type="number"
                          value={multiplier}
                          onChange={handleInputChange}
                          min={MIN_MULTIPLIER}
                          max={MAX_MULTIPLIER}
                          className="
                            w-20 h-12 text-center text-xl font-bold
                            border-2 border-gray-300 rounded-lg
                            focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                            [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                          "
                        />

                      </div>

                      <button
                        type="button"
                        onClick={handleIncrement}
                        disabled={multiplier >= MAX_MULTIPLIER}
                        className={`
                          w-10 h-10 rounded-full flex items-center justify-center
                          transition-all duration-200
                          ${multiplier >= MAX_MULTIPLIER
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-primary text-white hover:bg-primary-hover active:bg-primary-active'}
                        `}
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Quick Select Buttons */}
                    <div className="flex flex-wrap justify-center gap-2 mt-6">
                      {[1, 2, 5, 10, 20].map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setMultiplier(value)}
                          className={`
                            px-3 py-1.5 rounded-full text-sm font-medium
                            transition-all duration-200
                            ${multiplier === value
                              ? 'bg-primary text-white shadow-sm'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                          `}
                        >
                          ${value * AMOUNT_PER_UNIT}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Credit Card Section */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-foreground mb-3">Payment Details</h4>

                    {/* Card Number */}
                    <div className="mb-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Label className="text-sm mb-0">Card Number</Label>
                        <div className="flex gap-1">
                          {Object.entries(cardIcons).map(([type, src]) => (
                            <img
                              key={type}
                              src={src}
                              alt={`${type} icon`}
                              className={`w-8 h-5 ${isCardComplete && cardBrand !== ''
                                ? cardBrand === type
                                  ? 'block'
                                  : 'hidden'
                                : 'block'
                                }`}
                            />
                          ))}
                          {isCardComplete && cardBrand === '' && (
                            <img
                              src={RedCross}
                              alt="Invalid card"
                              className="w-8 h-5"
                            />
                          )}
                        </div>
                      </div>
                      <div className="border-2 border-muted rounded-lg px-3 py-2.5 bg-white">
                        <CardNumberElement options={cardStyle} onChange={handleCardChange} />
                      </div>
                    </div>

                    {/* Expiry and CVC */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <Label className="text-sm mb-1.5 block">Expiration</Label>
                        <div className="border-2 border-muted rounded-lg px-3 py-2.5 bg-white">
                          <CardExpiryElement options={cardStyle} />
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm mb-1.5 flex items-center gap-1">
                          CVC
                          <img src={BackofCard} alt="CVC" className="w-6 h-4" />
                        </Label>
                        <div className="border-2 border-muted rounded-lg px-3 py-2.5 bg-white">
                          <CardCvcElement options={cardStyle} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="large"
                    variant="default"
                    disabled={isSubmitting || !isCardComplete}
                    className="w-full mt-2"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Heart className="w-5 h-5" />
                        Donate ${totalAmount}
                      </span>
                    )}
                  </Button>

                  <div className="flex items-center justify-center gap-2 mt-3">
                    <PoweredByStripe width={80} height="auto" />
                    <span className="text-xs text-gray-500">Secure payment</span>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const CreateDonation = () => (
  <Elements stripe={stripePromise}>
    <CreateDonationForm />
  </Elements>
);

export default CreateDonation;